import { useGroupRoutines } from '@gripp/shared-logic';
import {
  AddButton,
  Colors,
  ColumnData,
  EditAlertForm,
  GroupAlertForm,
  LoadingIndicator,
  PanelShell,
  SlideAction
} from '@gripp/shared-ui';
import { StyleSheet, View } from 'react-native';
import { useNavigate, useParams } from 'react-router-dom';
import { useAlertFormColumn } from '../../shared/components/alerts/alertFormPanel';
import { GroupAlertListContent } from './group-alerts';
import { GroupsSelectionPanelMain } from './groupsSelectionPanel';

// ── Page ───────────────────────────────────────────────────────────────────────

export const GroupAlertFormPage = () => {
  const { groupId, alertId } = useParams<{
    groupId: string;
    alertId?: string;
  }>();

  const navigate = useNavigate();
  const { routinesCount, isLoading: isRoutinesLoading } = useGroupRoutines(groupId);

  const isEdit = !!alertId;
  const onBack = () => navigate('..', { relative: 'path' });

  const rightColumn = useAlertFormColumn({
    title: isEdit ? 'alerts.editAlert' : 'alerts.newAlert',
    onBack,
    renderForm: ({ formRef, renderBottomComponent, onRegisterBackGuard }) =>
      isEdit && alertId ? (
        <EditAlertForm
          alertId={alertId}
          groupId={groupId}
          onClose={onBack}
          formRef={formRef}
          renderBottomComponent={renderBottomComponent}
          onRegisterBackGuard={onRegisterBackGuard}
        />
      ) : (
        <GroupAlertForm
          groupId={groupId ?? ''}
          onClose={onBack}
          formRef={formRef}
          renderBottomComponent={renderBottomComponent}
        />
      ),
    column: { backButtonTransition: SlideAction.PREV },
  });

  if (!groupId) return null;

  const leftColumnMain = isRoutinesLoading ? (
    <View style={formStyles.loadingContainer}>
      <LoadingIndicator
        backgroundColor={Colors.role.canvas}
        flexContainer
      />
    </View>
  ) : routinesCount > 0 ? (
    <GroupAlertListContent
      groupId={groupId}
      selectedAlertId={alertId}
      isCreatingNew={!isEdit}
    />
  ) : (
    <GroupsSelectionPanelMain
      groupIds={[groupId]}
      onComplete={() => navigate('../../..', { relative: 'path' })}
    />
  );

  const leftColumn: ColumnData = {
    title: 'alerts.alerts',
    mainComponent: leftColumnMain,
    headerComponent: (
      <AddButton
        variant="header"
        onPress={() => navigate(`../new`, { relative: 'path' })}
        accessibilityLabel="Create Alert"
      />
    ),
    showBackButton: true,
    backButtonTransition: SlideAction.PREV,
    onBackButtonPress: () => {
      navigate('../../..', { relative: 'path' });
    },
    disableScroll: true,
  };

  return (
    <PanelShell
      leftColumn={leftColumn}
      rightColumnOverride={rightColumn}
    />
  );
};

// ── Styles ─────────────────────────────────────────────────────────────────────

const formStyles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    backgroundColor: Colors.role.canvas,
  },
});
